// ============================================================
// Repository katmanı
//
// Uygulamadaki HER yazma buradan geçer. Tek bir mekanizma iki işi
// birden yapar:
//
//   1. Kalıcılık — mutasyonlar depoya yazılır, açılışta tohum verinin
//      üzerine yeniden oynatılır. Sayfa yenilendiğinde veri kaybolmaz.
//   2. Denetim izi — aynı kayıt kim/ne zaman/öncesi/sonrası bilgisini
//      taşıdığı için ayrıca audit log tutmaya gerek kalmaz.
//
// Görünümler DATA'yı doğrudan değiştirmez; commit() çağırır.
// Depo adaptörü takılabilir: yapılandırma varsa Firestore, yoksa yerel.
// ============================================================

import { DATA } from "../data/store.js";
import { firebaseHazir } from "../config.js";
import { localAdapter } from "./adapters/local.js";
import { deleteAt, getAt, pushAt, setAt, yolAdi } from "./path.js";
import { projeksiyonCikar } from "./projeksiyon.js";
import { subeninKiracisi } from "../domain/context.js";

let adaptor = localAdapter();
let gunluk = [];
let uyari = null;
let dinleyiciBirak = null;

// ---------- Oturum sahibi ----------
// Auth gelene kadar tohum verideki kullanıcı; §2 ile gerçek kimliğe bağlanır.
let aktor = {
  uid: "anonim",
  name: "Oturum açılmadı",
  role: null,
};

export const aktoruAyarla = k => { aktor = { ...aktor, ...k }; };
export const aktorü = () => aktor;

// ---------- Mutasyon uygulama ----------
const UYGULA = {
  set: (kok, m) => setAt(kok, m.path, m.after),
  push: (kok, m) => pushAt(kok, m.path, m.after),
  delete: (kok, m) => deleteAt(kok, m.path),
};

/**
 * Eski yoklama yollarını güne bağlar.
 *
 * Yoklama mutasyonları bir dönem [...sube, "attendance", id] yolunu
 * kullanıyordu ve bu yol HER ZAMAN bugünün kaydına gidiyordu: dünkü
 * mutasyon bugün yeniden oynatılınca bugünün yoklamasını
 * işaretliyordu. BUGUN sabit olduğu sürece görünmedi — her gün aynı
 * gündü. Gerçek tarihe geçilince ortaya çıktı.
 *
 * Depoda o eski kayıtlardan var; onları mutasyonun KENDİ gününe
 * yönlendiriyoruz. Gün, damganın yerel karşılığından alınıyor: UTC
 * kullanılsaydı KKTC'de (UTC+3) gece yarısından sonraki üç saatte
 * bir önceki güne yazardı.
 */
function yoluGocur(mutasyon) {
  const yol = mutasyon.path;
  const i = yol.indexOf("attendance");
  // Yalnızca [..., "attendance", "<id>"] biçimi — son iki adım.
  if (i === -1 || i !== yol.length - 2) return yol;

  const gun = new Date(mutasyon.at).toLocaleDateString("sv-SE");
  return [...yol.slice(0, i), "attendanceLog", gun, yol[i + 1]];
}

function uygula(mutasyon) {
  const islem = UYGULA[mutasyon.op];
  if (!islem) throw new Error(`Bilinmeyen işlem: ${mutasyon.op}`);
  islem(DATA, { ...mutasyon, path: yoluGocur(mutasyon) });
}

// Aynı mutasyon iki kez oynatılmasın (canlı dinleme kendi yazdığımızı geri verir).
const gorulen = new Set();

// ---------- Genel API ----------

/**
 * Bir değişikliği uygular, günlüğe yazar ve denetim izine geçirir.
 *
 * @param {object} g
 * @param {"set"|"push"|"delete"} g.op
 * @param {Array} g.path      veri ağacındaki hedef (bkz. repo/path.js)
 * @param {*}     [g.after]   yeni değer (set/push)
 * @param {string} g.branchId
 * @param {string} g.entity   "attendance" | "message" | "staff" | ...
 * @param {string} [g.entityId]
 * @param {string} g.label    denetim ekranında görünecek insan okunur özet
 */
export function commit({ op, path, after, branchId, tenantId, entity, entityId, label }) {
  // Kiracı ya doğrudan verilir (kiracı seviyesindeki işlemler) ya da
  // şubeden çözülür.
  const kiraci = tenantId ? { id: tenantId } : subeninKiracisi(branchId);
  const mutasyon = {
    id: `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    actor: { ...aktor },
    tenantId: kiraci?.id ?? null,
    branchId: branchId ?? null,
    entity,
    entityId: entityId ?? null,
    op,
    path,
    before: op === "push" ? null : yapisalKopya(getAt(DATA, path)),
    after: after ?? null,
    label: label ?? `${entity} · ${yolAdi(path)}`,
  };

  uygula(mutasyon);
  gorulen.add(mutasyon.id);
  gunluk.push(mutasyon);

  // Depoya yazma arka planda; arayüz beklemez.
  //
  // İkinci parametre: mutasyonun Firestore BELGESİNE yansıması.
  // Günlük tek başına yeterli değil — sunucu tarafındaki sayımlar
  // (usage toplamları, plan limitleri) belgelere bakıyor ve günlük
  // onları tetiklemiyor. Bellekteki ağaç mutasyon uygulandıktan
  // SONRA okunuyor, yani belge güncel hâliyle yazılıyor.
  const projeksiyon = projeksiyonCikar(DATA, mutasyon);
  adaptor.ekle(mutasyon, projeksiyon).then(hata => { if (hata) uyari = hata; });

  return mutasyon;
}

/** Kolaylık sarmalayıcıları. */
export const setDeger = (g) => commit({ ...g, op: "set" });
export const ekleKayit = (g) => commit({ ...g, op: "push" });
export const silKayit = (g) => commit({ ...g, op: "delete" });

/**
 * Açılışta günlüğü tohum verinin üzerine oynatır.
 *
 * `kapsam` yalnızca Firestore adaptöründe anlamlı: hangi kiracının
 * hangi şubelerinin günlüğü okunacak. Yerel adaptör her şeyi tek
 * cihazda tuttuğu için parametreyi yok sayar.
 *
 * @param {{tenantId: string|null, branchIds: string[]}} [kapsam]
 */
export async function hydrate(kapsam) {
  if (firebaseHazir) {
    const { firestoreAdapter } = await import("./adapters/firestore.js");
    adaptor = firestoreAdapter();
  }

  let kayitlar = [];
  try {
    kayitlar = await adaptor.yukle(kapsam);
  } catch (e) {
    uyari = `Kayıtlı değişiklikler okunamadı: ${e.message}`;
    return { uygulanan: 0, atlanan: 0, uyari };
  }

  let uygulanan = 0;
  let atlanan = 0;
  for (const m of kayitlar) {
    if (gorulen.has(m.id)) continue;
    try {
      uygula(m);
      gorulen.add(m.id);
      gunluk.push(m);
      uygulanan++;
    } catch {
      // Tohum veri değiştiyse eski bir yol çözülemeyebilir; kayıt
      // denetim izinde kalır ama uygulanmaz.
      atlanan++;
    }
  }
  return { uygulanan, atlanan, uyari };
}

/** Başka cihazdaki değişiklikleri dinler (yalnızca Firestore adaptöründe). */
export function dinlemeyeBasla(tenantId, branchId, tazele) {
  dinlemeyiBirak();
  if (!adaptor.canli || !adaptor.dinle) return;
  dinleyiciBirak = adaptor.dinle(tenantId, branchId, yeniler => {
    let degisti = false;
    for (const m of yeniler) {
      if (gorulen.has(m.id)) continue;
      try { uygula(m); gorulen.add(m.id); gunluk.push(m); degisti = true; } catch { /* eski yol */ }
    }
    if (degisti) tazele();
  });
}

export function dinlemeyiBirak() {
  if (dinleyiciBirak) { dinleyiciBirak(); dinleyiciBirak = null; }
}

// ---------- Denetim izi (§8) ----------

/** Günlüğü yeniden eskiye, isteğe bağlı süzgeçlerle döner. */
export function denetimIzi({ tenantId, branchId, entity, entityId, since, limit = 200 } = {}) {
  return gunluk
    .filter(m => !tenantId || m.tenantId === tenantId)
    .filter(m => !branchId || m.branchId === branchId)
    .filter(m => !entity || m.entity === entity)
    .filter(m => !entityId || m.entityId === entityId)
    .filter(m => !since || m.at >= since)
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, limit);
}

export const gunlukBoyu = () => gunluk.length;
export const depoAdi = () => adaptor.ad;
export const depoUyarisi = () => uyari;

/** Yalnızca demo/test için: yerel günlüğü sıfırlar. */
export async function sifirla() {
  await adaptor.temizle();
  gunluk = [];
  gorulen.clear();
  uyari = null;
}

/** Test kancası — adaptörü değiştirir. */
export function adaptoruAyarla(yeni) {
  adaptor = yeni;
  gunluk = [];
  gorulen.clear();
}

// JSON'a girecek değerler kopyalanır; sonradan mutasyona uğramasın.
function yapisalKopya(v) {
  if (v == null || typeof v !== "object") return v ?? null;
  try { return JSON.parse(JSON.stringify(v)); } catch { return null; }
}
